import { debounce } from 'lodash';
import ajaxRequest from '../../helpers/ajaxRequest';
import classify from '../../helpers/classify';
import { CMS_MEDIA_CONTAINER } from '../youtubeVideo';
import { VIMEO_VIDEO, CMS_COMPONENT_VIMEO } from './index';

const loadVimeoScript = (): Promise<void> => {
  const tag = document.createElement('script');
  tag.src = 'https://player.vimeo.com/api/player.js';
  tag.async = true;
  const firstScriptTag = document.getElementsByTagName('script')[0];
  firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);

  return new Promise((resolve, reject) => {
    tag.addEventListener('error', () => reject());
    tag.addEventListener('load', () => resolve());
  });
};

const setVideoWidth = (videoEl: HTMLDivElement, height: number, width: number): void => {
  videoEl.style.width = '100%';
  videoEl.style.height = '100%';
  if (window.matchMedia('(min-width: 768px)').matches) {
    const videoWidth = videoEl.offsetHeight * (width / height);
    videoEl.style.width = `${videoWidth}px`;
  } else {
    const videoHeight = videoEl.offsetWidth * (height / width);
    videoEl.style.height = `${videoHeight}px`;
  }
};

const updateFullHeightVideoSize = (videoEl: HTMLDivElement): void => {
  ajaxRequest({
    method: 'GET',
    url: `https://vimeo.com/api/oembed.json?url=https%3A//vimeo.com/${videoEl.dataset.id}`
  })
    .then((videoParams) => {
      setVideoWidth(videoEl, videoParams.height, videoParams.width);
    })
    .catch((error) => {
      console.error(error);
      console.error(videoEl);

      // Fallback for failed request [IE11]
      setVideoWidth(videoEl, 9, 16);
    });
};

const setVideoHeight = (videoEl: HTMLDivElement, height: number, width: number): void => {
  const cmsComponent = videoEl.closest<HTMLDivElement>(classify(CMS_COMPONENT_VIMEO));
  const mediaContainers = cmsComponent?.querySelectorAll<HTMLDivElement>(classify(CMS_MEDIA_CONTAINER));
  let videoHeight: number;

  videoHeight = videoEl.offsetWidth * (height / width);

  if (cmsComponent && cmsComponent.classList.contains('cms-hero-promo')) {
    videoHeight = cmsComponent.offsetWidth * (height / width);
  }

  videoEl.style.height = `${videoHeight}px`;
  if (mediaContainers?.length) {
    mediaContainers.forEach((mediaContainer) => {
      mediaContainer.style.height = `${videoHeight}px`;
    });
  }
};

const updateVideoSize = (videoEl: HTMLDivElement): void => {
  ajaxRequest({
    method: 'GET',
    url: `https://vimeo.com/api/oembed.json?url=https%3A//vimeo.com/${videoEl.dataset.id}`
  })
    .then((videoParams) => {
      setVideoHeight(videoEl, videoParams.height, videoParams.width);
    })
    .catch((error) => {
      console.error(error);
      console.error(videoEl);

      // Fallback for failed request [IE11]
      setVideoHeight(videoEl, 9, 16);
    });
};

const handleAutopause = (videos: NodeListOf<HTMLDivElement>): void => {
  videos.forEach((video) => {
    video.onload = (): void => {
      const player = new window.Vimeo.Player(video);

      const pauseVideoOnTabOffScreen = (): void => {
        if (document.hidden) {
          player.pause();
        } else {
          player.play();
        }
      };
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            player.play();
            document.addEventListener('visibilitychange', pauseVideoOnTabOffScreen);
          } else {
            player.pause();
            document.removeEventListener('visibilitychange', pauseVideoOnTabOffScreen);
          }
        });
      });

      videoObserver.observe(video);
    };
  });
};

// eslint-disable-next-line import/prefer-default-export
export const initAllCmsVimeoVideos = (): void => {
  const vimeoVideos = document.querySelectorAll<HTMLDivElement>(classify(VIMEO_VIDEO));
  const fullHeightVimeoVideos = document.querySelectorAll<HTMLDivElement>(
    `.cms-combo-carousel--fullHeightImage .${VIMEO_VIDEO}`
  );

  if (!vimeoVideos.length) {
    return;
  }

  if (!window.Vimeo) {
    loadVimeoScript()
      .then(() => handleAutopause(vimeoVideos))
      .catch((error) => console.error(error));
  } else {
    handleAutopause(vimeoVideos);
  }

  const setResizeListener = (
    elements: HTMLDivElement[] | NodeListOf<HTMLDivElement>,
    callback: (videoEl: HTMLDivElement) => void
  ): void => {
    elements.forEach(callback);

    window.addEventListener(
      'resize',
      debounce(() => {
        elements.forEach(callback);
      }, 100)
    );
  };

  if (fullHeightVimeoVideos.length) {
    const vimeoVideosArr = Array.from(vimeoVideos);
    const fullHeightVimeoVideosArr = Array.from(fullHeightVimeoVideos);
    const nonFullHeightVimeoVideos = vimeoVideosArr.filter((video) => !fullHeightVimeoVideosArr.includes(video));

    setResizeListener(fullHeightVimeoVideos, updateFullHeightVideoSize);
    setResizeListener(nonFullHeightVimeoVideos, updateVideoSize);

    return;
  }

  setResizeListener(vimeoVideos, updateVideoSize);
};
